import { calculateOfferCargoProcessStatus } from "@/features/offer-cargo-process-status";
import { ScrollToTop } from "@/shared/lib/ScrollToTop";
import { BaseBreadCrumb } from "@/shared/ui/base-bread-crumb";
import { BaseDivider } from "@/shared/ui/base-divider";
import { OfferCargoDestinationsInfo } from "@/widgets/offer-cargo-destinations-info";
import { OfferCargoOfficeInfo } from "@/widgets/offer-cargo-office-info";
import { OfferCargoShippingFeeInfo } from "@/widgets/offer-cargo-shipping-fee-info";
import { OfferCargoStatusCard } from "@/widgets/offer-cargo-status-card/ui";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useNavigate } from "@tanstack/react-router";
import React, { useMemo } from "react";
import { DeliveryProposalRejectionDialog } from "../../../widgets/delivery-proposal-rejection-dialog";
import type { ShipperDeliveryProposalDetailPageProps } from "../model";
import { VehicleInfo } from "./vehicle-info";

export const ShipperDeliveryProposalDetailPageUi = (
  props: ShipperDeliveryProposalDetailPageProps,
) => {
  const { offerCargoProcess } = props;
  const [isRejectDialogOpen, setIsRejectDialogOpen] = React.useState(false);
  const navigate = useNavigate({ from: "/shipper/delivery-proposals/$id" });

  const links = useMemo(() => {
    if (offerCargoProcess.status !== "success") {
      return undefined;
    }
    const draftOfferCargoId = offerCargoProcess.data.draftOfferCargo.id;
    return [
      { href: "/shipper/cargos", label: "マイ荷物" },
      { href: `/shipper/cargos/${draftOfferCargoId}`, label: "荷物詳細" },
      { label: "配送提案詳細" },
    ];
  }, [offerCargoProcess.status, offerCargoProcess.data?.draftOfferCargo.id]);
  const deliveryProposalOfferCargo = useMemo(() => {
    if (offerCargoProcess.status !== "success") {
      return undefined;
    }
    return offerCargoProcess.data.deliveryProposalOfferCargos.find(
      (cargo) => cargo.id === props.deliveryProposalOfferCargoId,
    );
  }, [
    offerCargoProcess.status,
    offerCargoProcess.data?.deliveryProposalOfferCargos,
    props.deliveryProposalOfferCargoId,
  ]);
  const offerCargoProcessStatus = useMemo(() => {
    if (offerCargoProcess.status !== "success") {
      return undefined;
    }
    return calculateOfferCargoProcessStatus({
      process: offerCargoProcess.data,
    });
  }, [offerCargoProcess]);

  return (
    <Box px={{ xs: 2, sm: 4 }} pb={10}>
      <ScrollToTop />
      <BaseBreadCrumb links={links ?? []} isLoading={!links} sx={{ mb: 3 }} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
          }}
        >
          配送提案詳細
        </Typography>
      </Box>
      {offerCargoProcess.status === "error" ? (
        <Stack width="100%" justifyContent="center" alignItems="center" py={18}>
          <Typography variant="body1" color="error">
            エラーが発生しました
          </Typography>
          <Typography variant="caption" color="error">
            {offerCargoProcess.error?.message}
          </Typography>
        </Stack>
      ) : offerCargoProcess.status === "loading" ||
        !offerCargoProcessStatus ||
        !deliveryProposalOfferCargo ? (
        <Stack width="100%" justifyContent="center" alignItems="center" py={18}>
          <CircularProgress />
        </Stack>
      ) : (
        <Stack mt={{ xs: 3, sm: 4 }} spacing={{ xs: 3, sm: 4 }}>
          <DeliveryProposalRejectionDialog
            open={isRejectDialogOpen}
            onClose={() => setIsRejectDialogOpen(false)}
            onSubmit={(params) => {
              setIsRejectDialogOpen(false);
              props.onRejectClick(params);
            }}
          />
          <OfferCargoStatusCard
            variant="shipper_delivery_proposal_detail"
            offerCargoProcess={offerCargoProcess.data}
            deliveryProposalOfferCargoId={props.deliveryProposalOfferCargoId}
            onCreateContractClick={() => {
              props.onCreateContractClick();
            }}
            onRejectClick={() => {
              setIsRejectDialogOpen(true);
            }}
            onRecreateContractClick={() => {
              props.onRecreateContractClick();
            }}
            onConfirmContractClick={() => {
              if (offerCargoProcess.status !== "success") {
                return undefined;
              }
              const draftOfferCargoId =
                offerCargoProcess.data.draftOfferCargo.id;
              navigate({
                to: "/shipper/cargos/$cargoId",
                params: {
                  cargoId: draftOfferCargoId.toString(),
                },
              });
            }}
          />
          <BaseDivider />
          <Typography variant="h6" fontWeight={700}>
            提案内容
          </Typography>
          <OfferCargoShippingFeeInfo
            viewer="shipper"
            offerCargoProcess={offerCargoProcess.data}
            deliveryProposalOfferCargoId={props.deliveryProposalOfferCargoId}
          />
          <OfferCargoDestinationsInfo
            variant="shipper_delivery_proposal_detail"
            draftOfferCargo={offerCargoProcess.data.draftOfferCargo}
            deliveryProposalOfferCargo={deliveryProposalOfferCargo}
          />
          <VehicleInfo
            vehicleEquipmentMemo={
              deliveryProposalOfferCargo.vehicleEquipmentMemo
            }
            memo={deliveryProposalOfferCargo.memo}
          />
          <BaseDivider />
          <OfferCargoOfficeInfo
            variant="shipper_delivery_proposal_detail"
            deliveryProposalOfferCargo={deliveryProposalOfferCargo}
            offerCargoProcess={offerCargoProcess.data}
          />
        </Stack>
      )}
    </Box>
  );
};
